import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';

const CustomerTestimonials = ({ testimonials }) => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const testimonialsPerPage = 3;

  const filters = [
    { id: 'all', label: 'All Reviews' },
    { id: 'enterprise', label: 'Enterprise' },
    { id: 'sme', label: 'SME' },
    { id: 'government', label: 'Government' }
  ];

  const filteredTestimonials = selectedFilter === 'all' 
    ? testimonials 
    : testimonials?.filter(t => t?.companySize === selectedFilter);

  const totalPages = Math.ceil(filteredTestimonials?.length / testimonialsPerPage);
  const currentTestimonials = filteredTestimonials?.slice(
    currentPage * testimonialsPerPage,
    (currentPage + 1) * testimonialsPerPage
  );

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < rating ? "text-warning fill-current" : "text-muted-foreground/30"}
      />
    ));
  };

  return (
    <div className="bg-card py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">Customer Reviews</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See what our clients say about their experience with our ICT solutions
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {filters?.map((filter) => (
            <Button
              key={filter?.id}
              variant={selectedFilter === filter?.id ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setSelectedFilter(filter?.id);
                setCurrentPage(0);
              }}
            >
              {filter?.label}
            </Button>
          ))}
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {currentTestimonials?.map((testimonial) => (
            <div key={testimonial?.id} className="bg-muted/30 border border-border rounded-xl p-6">
              <div className="flex items-center space-x-1 mb-4">
                {renderStars(testimonial?.rating)}
              </div>
              
              <blockquote className="text-foreground mb-6 leading-relaxed">
                "{testimonial?.review}"
              </blockquote>
              
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-primary font-medium">
                    {testimonial?.name?.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-foreground">{testimonial?.name}</div>
                  <div className="text-sm text-muted-foreground">{testimonial?.position}</div>
                  <div className="text-sm text-muted-foreground">{testimonial?.company}</div>
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                  {testimonial?.companySize === 'enterprise' ? 'Enterprise' : 
                   testimonial?.companySize === 'sme' ? 'SME' : 'Government'}
                </span>
                <div className="text-xs text-muted-foreground">
                  {testimonial?.date}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              iconName="ChevronLeft"
            >
              Previous
            </Button>
            
            <div className="flex space-x-1">
              {Array.from({ length: totalPages }, (_, index) => (
                <Button
                  key={index}
                  variant={currentPage === index ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(index)}
                  className="w-10"
                >
                  {index + 1}
                </Button>
              ))}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage === totalPages - 1}
              iconName="ChevronRight"
              iconPosition="right"
            >
              Next
            </Button>
          </div>
        )}

        {/* Overall Rating Summary */}
        <div className="bg-muted/50 rounded-xl p-8 mt-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-primary mb-2">4.8</div>
              <div className="flex items-center justify-center space-x-1 mb-2">
                {renderStars(5)}
              </div>
              <div className="text-sm text-muted-foreground">Overall Rating</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2">127</div>
              <div className="text-sm text-muted-foreground">Total Reviews</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2">98%</div>
              <div className="text-sm text-muted-foreground">Satisfaction Rate</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2">95%</div>
              <div className="text-sm text-muted-foreground">Would Recommend</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerTestimonials;